import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Calendar, ExternalLink } from "lucide-react";

interface ScheduleInterviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId?: string;
  candidateName?: string;
  interviewType?: 'first' | 'second';
}

export function ScheduleInterviewDialog({ 
  open, 
  onOpenChange, 
  candidateId, 
  candidateName,
  interviewType = 'first' 
}: ScheduleInterviewDialogProps) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<any[]>([]);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    candidate_id: candidateId || "",
    interview_type: interviewType,
    scheduled_date: "",
    scheduled_time: ""
  });

  useEffect(() => {
    if (open && !candidateId) {
      fetchCandidates();
    }
  }, [open, candidateId]);

  useEffect(() => {
    if (candidateId) {
      setFormData(prev => ({ ...prev, candidate_id: candidateId }));
    }
  }, [candidateId]);

  const fetchCandidates = async () => {
    try {
      const { data, error } = await supabase
        .from('candidates')
        .select('id, full_name, status')
        .in('status', ['applied', 'first_interview_scheduled', 'first_interview_completed'])
        .order('full_name');

      if (error) throw error;
      setCandidates(data || []);
    } catch (error: any) {
      console.error('Error fetching candidates:', error);
    }
  };

  const generateOutlookUrl = (interviewData: any) => {
    const candidate = candidates.find(c => c.id === interviewData.candidate_id);
    const startDate = new Date(`${interviewData.scheduled_date}T${interviewData.scheduled_time}`);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour later

    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const subject = encodeURIComponent(`${interviewData.interview_type.charAt(0).toUpperCase() + interviewData.interview_type.slice(1)} Interview - ${candidate?.full_name || candidateName}`);
    const body = encodeURIComponent(`Interview scheduled with ${candidate?.full_name || candidateName}\n\nInterview Type: ${interviewData.interview_type.charAt(0).toUpperCase() + interviewData.interview_type.slice(1)} Interview\n\nPlease prepare interview materials accordingly.`);
    
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${subject}&body=${body}&startdt=${formatDate(startDate)}&enddt=${formatDate(endDate)}`;
    return outlookUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
      
      const { error } = await supabase
        .from('interviews')
        .insert([{
          candidate_id: formData.candidate_id,
          interview_type: formData.interview_type,
          scheduled_date: scheduledDateTime.toISOString(),
          status: 'scheduled'
        }]);

      if (error) throw error;

      // Update candidate status
      const newStatus = formData.interview_type === 'first' ? 'first_interview_scheduled' : 'second_interview_scheduled';
      await supabase
        .from('candidates')
        .update({ status: newStatus })
        .eq('id', formData.candidate_id);

      // Outlook integration removed as requested

      toast({
        title: "Επιτυχία",
        description: "Η συνέντευξη προγραμματίστηκε επιτυχώς",
      });

      // Reset form
      setFormData({
        candidate_id: candidateId || "",
        interview_type: 'first',
        scheduled_date: "",
        scheduled_time: ""
      });

      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message || "Αποτυχία προγραμματισμού συνέντευξης",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[500px]" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Προγραμματισμός Συνέντευξης</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {!candidateId && (
            <div className="space-y-2">
              <Label htmlFor="candidate">Υποψήφιος *</Label>
              <Select value={formData.candidate_id} onValueChange={(value) => handleInputChange('candidate_id', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Επιλέξτε υποψήφιο" />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((candidate) => (
                    <SelectItem key={candidate.id} value={candidate.id}>
                      {candidate.full_name} - {candidate.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {candidateId && candidateName && (
            <div className="space-y-2">
              <Label>Υποψήφιος</Label>
              <div className="p-2 bg-muted rounded-md">
                {candidateName}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="interview_type">Τύπος Συνέντευξης *</Label>
            <Select value={formData.interview_type} onValueChange={(value) => handleInputChange('interview_type', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="first">Πρώτη Συνέντευξη</SelectItem>
                <SelectItem value="second">Δεύτερη Συνέντευξη</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduled_date">Ημερομηνία *</Label>
              <Input
                id="scheduled_date"
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => handleInputChange('scheduled_date', e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="scheduled_time">Ώρα *</Label>
              <Input
                id="scheduled_time"
                type="time"
                value={formData.scheduled_time}
                onChange={(e) => handleInputChange('scheduled_time', e.target.value)}
                required
              />
            </div>
          </div>


          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Ακύρωση
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Προγραμματισμός..." : "Προγραμματισμός Συνέντευξης"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}